/**
 * RulerManager.js
 * 
 * This module provides functionality for creating and managing a ruler tool
 * in the 3D environment of the SightFlight Designer application. The ruler allows
 * users to measure distances between points in the 3D scene, displaying the
 * measurement in feet.
 * 
 * The RulerManager class handles mouse interactions, visual updates, and
 * measurement calculations for the ruler tool.
 */

import {DrawingGlobals, LABELS} from "../Global/variables";
import {BufferGeometry, LineSegments} from 'three';
import {LINES} from "../Global/materials";
import {removeLineMeshes} from "../Global/functions";
import {createRuler} from "./RulerStore";

class RulerManager {
    /**
     * Creates a new instance of the RulerManager
     * 
     * @param {THREE.Scene} scene - The Three.js scene where the ruler will be displayed
     * @param {Function} stopDrawing - Callback function to exit the ruler mode
     */
    constructor(scene, stopDrawing) {
        this.scene = scene;             // Reference to the 3D scene
        this.isRulerStarted = false;    // Flag to track if ruler measurement has started
        this.stopDrawing = stopDrawing; // Callback to exit ruler mode
    }

    /**
     * Handles mouse down events during ruler operation
     *
     * The first click starts the ruler by setting the start point.
     * The second click commits the measurement and exits ruler mode.
     *
     * @param {THREE.Vector3} point - The 3D point where the mouse was clicked
     */
    handleMouseDown(point) {
        if (!this.isRulerStarted) {
            // First click - start the ruler
            this.isRulerStarted = true;
            this.setRuler(point);
        } else {
            // Second click - hand the finished span to the ruler store so it
            // stays in the scene, then drop the rubber-band preview.
            this.commitRuler(point);
            this.removeRuler();
            this.stopDrawing();
        }
    }

    /**
     * Turns the in-progress measurement into a persistent ruler
     *
     * The preview line lives in DrawingGlobals (shared with segment drawing) and
     * is torn down right after this; the persistent copy is owned by RulerStore.
     *
     * @param {THREE.Vector3} end - The 3D point where the measurement ends
     */
    commitRuler(end) {
        const start = DrawingGlobals.intersectionPoints[0];
        if (!start || !end) return;
        createRuler(this.scene, start, end, { recordUndo: true });
    }

    /**
     * Handles mouse move events during ruler operation
     * 
     * After the ruler has started, this updates the end point of the
     * ruler line and the measurement label as the mouse moves.
     * 
     * @param {THREE.Vector3} point - The current 3D position of the mouse pointer
     */
    handleMouseMove(point) {
        if (this.isRulerStarted) {
            // Update the ruler line to end at the current mouse position
            this.updateRuler(point);
            // Update the measurement label to show the current distance
            this.addMeasurementLabel(point);
        }
    }

    /**
     * Creates the initial ruler line at the start point
     * 
     * This function creates a line segment with both start and end points
     * at the same location. The end point will be updated as the mouse moves.
     * 
     * @param {THREE.Vector3} point - The 3D point where the ruler starts
     */
    setRuler(point) {
        // Add the start point to the global intersection points array
        DrawingGlobals.intersectionPoints.push(point);
        
        // Create a geometry with two identical points (will be updated later)
        let geometry = new BufferGeometry().setFromPoints([
            DrawingGlobals.intersectionPoints.slice(-1)[0],
            DrawingGlobals.intersectionPoints.slice(-1)[0],
        ]);
        
        // Create a line segment with the (blue) ruler preview material
        let line = new LineSegments(geometry, LINES.rulerLineMaterial);
        // Ensure the line is always visible, regardless of view frustum
        line.frustumCulled = false;
        
        // Add the line to the global line meshes array
        DrawingGlobals.lineMeshes.push(line);
        
        // Add the line to the scene for rendering
        this.scene.add(DrawingGlobals.lineMeshes.slice(-1)[0]);
    }

    removeRuler() {
        // Hide the angle/measurement label
        LABELS.angle.visible = false;
        this.isRulerStarted = false;

        // Clean up only if there are intersection points
        if (DrawingGlobals.intersectionPoints.length > 0) {
            // Remove all line meshes from the scene
            removeLineMeshes(this.scene);
            
            // Clear the global arrays
            DrawingGlobals.intersectionPoints.length = 0;
            DrawingGlobals.lineMeshes.length = 0;
        }
    }

    /**
     * Updates the end point of the ruler line as the mouse moves
     * 
     * This function modifies the position buffer of the line geometry
     * to make the end point follow the current mouse position.
     * 
     * @param {THREE.Vector3} point - The current 3D position of the mouse pointer
     */
    updateRuler(point) {
        // Get the most recent line mesh from the global array
        const lineMesh = DrawingGlobals.lineMeshes.slice(-1)[0];
        
        // Update the position of the second point (indices 3, 4, 5 in the position array)
        // The position buffer stores x,y,z coordinates for each point sequentially
        lineMesh.geometry.attributes.position.array[3] = point.x;
        lineMesh.geometry.attributes.position.array[4] = point.y;
        lineMesh.geometry.attributes.position.array[5] = point.z;
        
        // Mark the position buffer as needing an update
        lineMesh.geometry.attributes.position.needsUpdate = true;
    }

    /**
     * Updates the measurement label with the current distance
     * 
     * This function calculates the distance between the start and end points,
     * converts it to feet, and displays it in the label at the midpoint of the line.
     * 
     * @param {THREE.Vector3} end - The current end point of the ruler
     */
    addMeasurementLabel(end) {
        // Get the start point from the global intersection points array
        let start = DrawingGlobals.intersectionPoints[0];
        
        // Make the measurement label visible
        LABELS.angle.visible = true;
        
        // Calculate the distance in meters and convert to feet (1 meter = 3.281 feet)
        let dist = start.distanceTo(end) * 3.281;
        
        // Update the label text with the distance in feet, rounded to 2 decimal places
        LABELS.angle.element.innerText = dist.toFixed(2) + " ft";
        
        // Position the label at the midpoint of the line
        LABELS.angle.position.lerpVectors(start, end, 0.5);
    }
}

export default RulerManager;